import { useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fireHeartsConfetti } from "@/lib/confetti";
import AiAssistButton from "@/components/AiAssistButton";
import { ExternalLink as ExternalLinkIcon, Car as CarIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DollarSign, Calendar, Clock, MapPin, Loader2, X, Check } from "lucide-react";

export default function RespondOfferModal({ isOpen, onClose, offer, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null); // 'accept', 'decline', 'counter'
  const [counterAmount, setCounterAmount] = useState("");
  const [counterMessage, setCounterMessage] = useState("");

  const handleRespond = async (selectedAction) => {
    setLoading(true);

    try {
      const payload = {
        action: selectedAction
      };

      if (selectedAction === "counter") {
        const amount = parseFloat(counterAmount);
        if (amount < 100 || amount > 1000) {
          toast.error("Counter amount must be between $100 and $1000");
          setLoading(false);
          return;
        }
        payload.counter_amount = amount;
        payload.counter_message = counterMessage;
      }

      await api.post(`/date-offers/${offer.offer_id}/respond`, payload);

      if (selectedAction === "accept") {
        toast.success("Offer accepted! Your date will be asked to complete payment.");
        fireHeartsConfetti();
      } else if (selectedAction === "decline") {
        toast.success("Offer declined");
      } else if (selectedAction === "counter") {
        toast.success("Counter-offer sent!");
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond");
    } finally {
      setLoading(false);
    }
  };

  if (!offer) return null;

  const man = offer.man || {};

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Date Offer from {man.name}</DialogTitle>
          <DialogDescription>
            Review and respond to this date offer
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Man's Profile */}
          <div className="bg-primary/10 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <img
                src={man.photos?.[0] || man.picture || "/placeholder.svg"}
                alt={man.name}
                className="h-16 w-16 rounded-full object-cover"
              />
              <div>
                <p className="font-semibold text-lg">{man.name}</p>
                <p className="text-sm text-muted-foreground">{man.age} years old</p>
              </div>
            </div>
            {man.bio && (
              <p className="text-sm text-muted-foreground">{man.bio}</p>
            )}
          </div>

          {/* Offer Details */}
          <div className="space-y-3">
            <div className="bg-green-500/10 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-1">Offer Amount</p>
              <p className="text-3xl font-bold text-green-500">
                ${offer.offer_amount}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                You&apos;ll receive ${(offer.offer_amount * 0.8 + (offer.date_details?.transport_allowance || 0)).toFixed(2)} (80%{offer.date_details?.transport_allowance > 0 ? ` + $${offer.date_details.transport_allowance} transport` : ""})
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Date:</span>
                <span>{offer.date_details?.proposed_date}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Time:</span>
                <span>{offer.date_details?.proposed_time}</span>
              </div>

              {offer.date_details?.duration_hours ? (
                <div className="flex items-center gap-2 text-sm" data-testid="respond-offer-duration">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Duration:</span>
                  <span>{offer.date_details.duration_hours} hour{offer.date_details.duration_hours === 1 ? "" : "s"}</span>
                </div>
              ) : null}

              {offer.date_details?.date_type && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">Type:</span>
                  <span className="capitalize">{offer.date_details.date_type.replace('_', ' ')}</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Location:</span>
                <span>{offer.date_details?.location}</span>
              </div>

              {offer.date_details?.transport_allowance > 0 && (
                <div className="flex items-center gap-2 text-sm" data-testid="respond-transport-line">
                  <CarIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Transport:</span>
                  <span>+${offer.date_details.transport_allowance} Uber/taxi allowance — 100% yours</span>
                </div>
              )}

              {offer.date_details?.experience_url && (
                <a href={offer.date_details.experience_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-rose-400 hover:underline" data-testid="respond-experience-link">
                  <ExternalLinkIcon className="h-4 w-4" /> View the planned Airbnb Experience
                </a>
              )}
            </div>

            {offer.date_details?.message && (
              <div className="bg-muted rounded-lg p-3">
                <p className="text-sm font-medium mb-1">Message:</p>
                <p className="text-sm text-muted-foreground">
                  &quot;{offer.date_details.message}&quot;
                </p>
              </div>
            )}
          </div>

          {/* Action Selection */}
          {!action && (
            <div className="space-y-2 pt-4">
              <Button 
                className="w-full" 
                size="lg"
                onClick={() => setAction("accept")}
                data-testid="accept-offer-btn"
              >
                <Check className="mr-2 h-4 w-4" />
                Accept Offer
              </Button>

              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => setAction("counter")}
              >
                <DollarSign className="mr-2 h-4 w-4" />
                Counter Offer
              </Button>

              <Button 
                className="w-full" 
                variant="destructive"
                onClick={() => setAction("decline")}
              >
                <X className="mr-2 h-4 w-4" />
                Decline
              </Button>
            </div>
          )}

          {/* Accept Confirmation */}
          {action === "accept" && (
            <div className="space-y-4 pt-4 border-t">
              <div className="bg-green-500/10 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Confirm Acceptance</p>
                <p className="text-xs text-muted-foreground">
                  By accepting, {man.name} will be notified to complete payment. 
                  You&apos;ll receive ${(offer.offer_amount * 0.8 + (offer.date_details?.transport_allowance || 0)).toFixed(2)} after payment confirmation.
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setAction(null)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => handleRespond("accept")}
                  disabled={loading}
                  className="flex-1"
                  data-testid="confirm-accept-btn"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirm Accept
                </Button>
              </div>
            </div>
          )}

          {/* Decline Confirmation */}
          {action === "decline" && (
            <div className="space-y-4 pt-4 border-t">
              <div className="bg-red-500/10 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Confirm Decline</p>
                <p className="text-xs text-muted-foreground">
                  This will decline the offer and free up their slot.
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setAction(null)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => handleRespond("decline")}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirm Decline
                </Button>
              </div>
            </div>
          )}

          {/* Counter Offer Form */}
          {action === "counter" && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label htmlFor="counter_amount">
                  <DollarSign className="inline h-4 w-4" /> Counter Amount *
                </Label>
                <Input
                  id="counter_amount"
                  type="number"
                  min="100"
                  max="1000"
                  step="10"
                  placeholder="Enter amount (100-1000)"
                  value={counterAmount}
                  onChange={(e) => setCounterAmount(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  You&apos;ll receive ${counterAmount ? (counterAmount * 0.8).toFixed(2) : "0"} (80%) if accepted
                </p>
              </div>

              <div>
                <Label htmlFor="counter_message">Message (Optional)</Label>
                <Textarea
                  id="counter_message"
                  placeholder="Explain your counter-offer..."
                  value={counterMessage}
                  onChange={(e) => setCounterMessage(e.target.value)}
                  rows={3}
                />
                <div className="mt-2">
                  <AiAssistButton
                    context="counter_message"
                    receiverId={offer?.from_user_id}
                    extra={{ amount: counterAmount }}
                    onPick={setCounterMessage}
                    label="AI Wingman: write it for me"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setAction(null)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => handleRespond("counter")}
                  disabled={loading || !counterAmount}
                  className="flex-1"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Counter
                </Button>
              </div>
            </div>
          )}
        </div>

        {!action && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} className="w-full">
              Close
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
